import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { colors } from '../../tokens';

export interface AppHeaderTextProps extends Omit<TypographyProps, 'variant' | 'component'> {
  /**
   * Color variant for the app header text
   * - primary: Pure white (#ffffff) - default and most common
   * - secondary: Light gray for less emphasis
   */
  colorVariant?: 'primary' | 'secondary';
}

/**
 * AppHeaderText Component
 * 
 * Typography component specifically designed for app/page headers in the navigation bar.
 * This is distinct from hero headings and section headings - it's the consistent style
 * used for page titles in the AppHeader component (e.g., "Catalog", "Dashboard", "My Songs").
 * 
 * Specifications:
 * - Font size: 1.25rem (20px) - Material-UI h6 variant
 * - Font weight: 700 (bold)
 * - Line height: 1.2
 * - Color: Pure white (#ffffff) by default
 * 
 * Usage:
 * - Page titles in AppHeader
 * - Main navigation headers
 * - Modal/dialog titles in navigation context
 * 
 * @example
 * <AppHeaderText>Catalog</AppHeaderText>
 * <AppHeaderText>My Songs</AppHeaderText>
 * <AppHeaderText colorVariant="secondary">Settings</AppHeaderText>
 */
export const AppHeaderText: React.FC<AppHeaderTextProps> = ({ 
  children, 
  colorVariant = 'primary',
  sx,
  ...props 
}) => {
  const colorMap = {
    primary: colors.text.primary,
    secondary: colors.text.secondary,
  };

  return (
    <Typography
      component="h1"
      variant="h6"
      sx={{
        lineHeight: 1.2,
        fontWeight: 700,
        color: colorMap[colorVariant],
        ...sx,
      }}
      {...(props as any)}
    >
      {children}
    </Typography>
  );
};
